/** UI helpers shared by Pregled and Mesta (day tones, coverage tones). */

export interface DayTone {
  ink: string;
  mid: string;
  tint: string;
  bd: string;
  wash: string;
  header: DayHeaderTone;
}

/**
 * The global header's ramp for one day. The header wash is the lightest step of
 * the day's hue, so the coverage bar and the status figures on top of it still
 * read as status rather than as part of the ground; everything in the header
 * that would otherwise be grey (`rail`, `track`, `covTrack`, `btnBd`) takes the
 * same hue, because a neutral grey on a tinted ground reads as dirt.
 *
 * Chroma is tuned per hue rather than held flat: a pale gold reads weaker than a
 * pale teal at the same number, so hue 95 carries more of it.
 */
export interface DayHeaderTone {
  /** Bar background. */
  bar: string;
  /** Bottom border, and the divider after the wordmark. */
  rail: string;
  /** Track behind the day picker. */
  track: string;
  /** Unfilled part of the coverage bar. */
  covTrack: string;
  /** Border of the outline buttons sitting on the wash. */
  btnBd: string;
  /** Dot on the day-picker buttons. */
  dot: string;
  /** Label of the selected day-picker button. */
  ink: string;
}

const DAYTONE: DayTone[] = [
  {
    ink: 'oklch(0.45 0.062 205)',
    mid: 'oklch(0.68 0.075 205)',
    tint: 'oklch(0.955 0.022 205)',
    bd: 'oklch(0.88 0.042 205)',
    wash: 'oklch(0.975 0.009 205)',
    header: {
      bar: 'oklch(0.97 0.024 205)',
      rail: 'oklch(0.87 0.05 205)',
      track: 'oklch(0.935 0.032 205)',
      covTrack: 'oklch(0.91 0.025 205)',
      btnBd: 'oklch(0.86 0.03 205)',
      dot: 'oklch(0.62 0.1 205)',
      ink: 'oklch(0.35 0.07 205)',
    },
  },
  {
    ink: 'oklch(0.44 0.048 95)',
    mid: 'oklch(0.7 0.055 95)',
    tint: 'oklch(0.958 0.02 95)',
    bd: 'oklch(0.89 0.038 95)',
    wash: 'oklch(0.976 0.009 95)',
    header: {
      bar: 'oklch(0.97 0.038 95)',
      rail: 'oklch(0.87 0.05 95)',
      track: 'oklch(0.935 0.045 95)',
      covTrack: 'oklch(0.91 0.035 95)',
      btnBd: 'oklch(0.86 0.04 95)',
      dot: 'oklch(0.68 0.1 95)',
      ink: 'oklch(0.4 0.07 95)',
    },
  },
  {
    ink: 'oklch(0.45 0.07 315)',
    mid: 'oklch(0.68 0.08 315)',
    tint: 'oklch(0.958 0.022 315)',
    bd: 'oklch(0.89 0.042 315)',
    wash: 'oklch(0.976 0.009 315)',
    header: {
      bar: 'oklch(0.97 0.026 315)',
      rail: 'oklch(0.87 0.05 315)',
      track: 'oklch(0.935 0.034 315)',
      covTrack: 'oklch(0.91 0.026 315)',
      btnBd: 'oklch(0.86 0.03 315)',
      dot: 'oklch(0.62 0.1 315)',
      ink: 'oklch(0.35 0.07 315)',
    },
  },
  {
    ink: 'oklch(0.45 0.06 265)',
    mid: 'oklch(0.68 0.07 265)',
    tint: 'oklch(0.958 0.022 265)',
    bd: 'oklch(0.88 0.04 265)',
    wash: 'oklch(0.976 0.009 265)',
    header: {
      bar: 'oklch(0.97 0.026 265)',
      rail: 'oklch(0.87 0.048 265)',
      track: 'oklch(0.935 0.034 265)',
      covTrack: 'oklch(0.91 0.026 265)',
      btnBd: 'oklch(0.86 0.03 265)',
      dot: 'oklch(0.62 0.09 265)',
      ink: 'oklch(0.35 0.07 265)',
    },
  },
];

export const dayToneAt = (index: number): DayTone => DAYTONE[index % DAYTONE.length]!;

/** Coverage % → [bg, border, fg]: green ≥ 97, amber ≥ 85, red below. */
export function covTone(pct: number): [string, string, string] {
  if (pct >= 97)
    return ['oklch(0.955 0.035 155)', 'oklch(0.86 0.05 155)', 'oklch(0.36 0.07 155)'];
  if (pct >= 85) return ['oklch(0.975 0.03 75)', 'oklch(0.9 0.05 75)', 'oklch(0.44 0.09 75)'];
  return ['oklch(0.97 0.025 25)', 'oklch(0.89 0.05 25)', 'oklch(0.45 0.12 25)'];
}

/** '6:30' — hour not zero-padded. */
export const m2t = (m: number) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

/** '4 h 30 min' / '4 h'. */
export function dur(min: number): string {
  const h = Math.floor(min / 60);
  const mm = min % 60;
  return mm ? `${h} h ${mm} min` : `${h} h`;
}

/** Accusative day name: 'Sobota' → 'soboto', 'Petek' → 'petek'. */
export function dayAcc(label: string) {
  const w = label.trim().toLowerCase();
  const irregular: Record<string, string> = { sobota: 'soboto', nedelja: 'nedeljo', sreda: 'sredo' };
  if (irregular[w]) return irregular[w];
  return w.endsWith('a') ? `${w.slice(0, -1)}o` : w;
}

/** Genitive with preposition: 'Sobota' → 's sobote'. */
export function dayFrom(label: string) {
  const w = label.trim().toLowerCase();
  const map: Record<string, string> = {
    sobota: 's sobote',
    nedelja: 'z nedelje',
    petek: 's petka',
    sreda: 's srede',
    četrtek: 's četrtka',
    torek: 'iz torka',
    ponedeljek: 's ponedeljka',
  };
  return map[w] ?? `z dneva ${w}`;
}

/** Initials for the avatar dot. */
export function ini(name: string) {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]!.toUpperCase()).join('') || '?';
}

/**
 * Slovenian counts in four — 1, 2, 3–4, 5 and up — which every `pl…` below is
 * one filled-in copy of. Returns the word alone; `plN` puts the number on it.
 */
export function plural(n: number, one: string, two: string, few: string, many: string) {
  const r = n % 100;
  return r === 1 ? one : r === 2 ? two : r === 3 || r === 4 ? few : many;
}

/** The same, counted: '3 izmene'. */
export const plN = (n: number, one: string, two: string, few: string, many: string) =>
  `${n} ${plural(n, one, two, few, many)}`;

/** '3 izmene' — Slovenian plural for izmena. */
export const plS = (n: number) => plN(n, 'izmena', 'izmeni', 'izmene', 'izmen');

/** '3 dni' — Slovenian plural for dan. */
export const plDni = (n: number) => plN(n, 'dan', 'dneva', 'dni', 'dni');

/** '3 dodelitve' — Slovenian plural for dodelitev. */
export const plDod = (n: number) =>
  plN(n, 'dodelitev', 'dodelitvi', 'dodelitve', 'dodelitev');

/** '3 predlogi' — Slovenian plural for predlog. */
export const plPred = (n: number) => plN(n, 'predlog', 'predloga', 'predlogi', 'predlogov');

/** '2 vrzeli' — Slovenian plural for vrzel. */
export const plVrz = (n: number) => `${n} ${n % 100 === 1 ? 'vrzel' : 'vrzeli'}`;

/** Slovenian count of track positions: 1 točka, 2 točki, 3-4 točke, 5+ točk. */
export const plT = (n: number) => plN(n, 'točka', 'točki', 'točke', 'točk');

/** 'danes' / 'včeraj' / 'pred N dnevi'. */
export const ago = (d: number) => (d === 0 ? 'danes' : d === 1 ? 'včeraj' : `pred ${d} dnevi`);

/**
 * How long ago a timestamp was, in the coarsest unit that still says
 * something: the question is always "recently or not", never the second.
 * `null` for a moment that never happened.
 */
export function sinceLabel(iso: string | null): string | null {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'pravkar';
  if (mins < 60) return `pred ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `pred ${hours} h`;
  // 'pred' takes the instrumental, as `ago` above already spells it.
  return ago(Math.floor(hours / 24));
}

export type DayPhase = 'past' | 'live' | 'next';

/** Event-day phase relative to today's local date (YYYY-MM-DD compare). */
export function dayPhase(date: string, today: string): DayPhase {
  if (date < today) return 'past';
  if (date === today) return 'live';
  return 'next';
}

export const phaseLabel: Record<DayPhase, string> = {
  past: 'končano',
  live: 'danes',
  next: 'pred nami',
};

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** '3 osebe' — Slovenian plural for oseba. */
export const plOs = (n: number) => plN(n, 'oseba', 'osebi', 'osebe', 'oseb');

/** '3 mesta' — Slovenian plural for (redarsko) mesto. */
export const plM = (n: number) => plN(n, 'mesto', 'mesti', 'mesta', 'mest');

/** '3 ekipe' — Slovenian plural for ekipa. */
export const plEk = (n: number) => plN(n, 'ekipa', 'ekipi', 'ekipe', 'ekip');
